<template>
  <div class="my-3">
    <div class="signup card card-body border-0 shadow-sm p-4 mb-4" style="max-width:640px;">
      <div v-if="errorStatus" class="alert alert-danger my-3" role="alert">{{ errorStatus }}</div>
      <template v-if="hasCreatedAccount">
        <div class="alert alert-success my-3" role="alert">

          <i18n-t keypath="signin_term" tag="label" for="signin_link">
            <a class="alert-link"  href="#"  onclick="window.dialogManager?.showSignIn(); return false;">{{ $t('signin_link') }}</a>
          </i18n-t>
        </div>
        <div v-if="autoLoginFailed" class="alert alert-warning my-1" role="alert">
          {{ $t('autologin_failed') }}
        </div>
      </template>
      <template v-else>
        <!-- Honeypot field - hidden from humans, bots will fill it -->
        <div style="position:absolute;left:-9999px;" aria-hidden="true">
          <label for="input-website">Website</label>
          <input v-model="honeypot" type="text" id="input-website" name="website" tabindex="-1" autocomplete="off">
        </div>
        <div class="row">
          <div class="col-12 col-md-10 col-lg-8">
            <div class="mb-3">
              <label for="input-email" class="form-label" :disabled="hasValidatedEmail">{{ $t('accountemail') }}</label> <!-- Account email-->
              <input v-model.trim="email" ref="emailInput" class="form-control email" id="input-email" type="email" :placeholder="$t('emailprompt')" :disabled="hasValidatedEmail" @keyup.enter="hasValidEmail && !isValidatingCaptcha && requestEmailValidation(false)">
            </div>
          </div>
        </div>
        <div v-if="!hasValidatedEmail" class="row">
          <div class="col-12">
            <button type="button" class="btn btn-primary" :disabled="!hasValidEmail || isValidatingCaptcha" @click="requestEmailValidation(false)">
              <span v-if="isValidatingCaptcha" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{$t('continue')}}
            </button>
            <a v-if="showCancel" class="alert-link px-2"  href="#" @click="cancel()">{{ $t('cancel') }}</a>
          </div>
          <div class="col-12 mt-2">
            <i18n-t keypath="signinPrompt" tag="label" for="signin_link">
              <a class="alert-link"  href="#"  onclick="window.dialogManager?.showSignIn(); return false;">{{ $t('signin_link') }}</a>
            </i18n-t>

           
          </div>
        </div>
        <template v-if="hasRequestedAuthCode && !hasValidatedAuthCode">
        <div class="row">
          <div class="col-12 col-md-10 col-lg-8">
              <div class="mb-3">
                <label for="input-authcode" class="form-label">{{ $t('validationcode') }}</label>
                <input v-model.trim="authCode" ref="authCodeInput" class="form-control" id="input-authcode" type="text" autocomplete="one-time-code" inputmode="numeric" maxlength="5" :placeholder="$t('validationcodeprompt')">
                <div class="form-text">
                 
                    <i18n-t keypath="requestanothercode_term" tag="span" for="requestanothercode_link">
                      <a href="#" @click="requestEmailValidation(true)">{{ $t('requestanothercode_link') }}</a>
                    </i18n-t>
                    
                </div>
                <div v-if="errorStatus" class="text-danger">{{errorStatus}}</div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="button" class="btn btn-primary" :disabled="!hasValidEmail || isValidatingAuthCode" @click="requestValidateValidationCode">
                <span v-if="isValidatingAuthCode" class="spinner-border spinner-border-sm me-1" role="status"></span>
                {{$t('continue')}}
              </button>
              <a v-if="showCancel" class="alert-link px-2"  href="#" @click="cancel()">{{ $t('cancel') }}</a>
            </div>
          </div>
        </template>
        <template v-if="hasValidatedAuthCode">
          <div class="row mb-3">
            <div class="col-12 col-md-10 col-lg-8 mb-2">
              <label class="form-label" for="pass-visibility1">{{$t('password')}}</label>
              <div class="input-group">
                <input v-model="password1" ref="passwordInput" class="form-control" :type="showPassword ? 'text' : 'password'" id="pass-visibility1" autocomplete="new-password" :placeholder="$t('enterpassword')">
                <button class="btn btn-outline-secondary" type="button" @click="showPassword = !showPassword" :title="showPassword ? $t('hidepassword') : $t('showpassword')">
                  <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
              </div>
              <!-- Inline password strength indicator -->
              <div v-if="password1" class="mt-1">
                <div class="small">
                  <span :class="password1.length >= 8 ? 'text-success' : 'text-danger'">
                    <i class="bi" :class="password1.length >= 8 ? 'bi-check-circle-fill' : 'bi-x-circle'"></i> {{ $t('pwd_length') }}
                  </span><br>
                  <span :class="/[A-Z]/.test(password1) ? 'text-success' : 'text-danger'">
                    <i class="bi" :class="/[A-Z]/.test(password1) ? 'bi-check-circle-fill' : 'bi-x-circle'"></i> {{ $t('pwd_uppercase') }}
                  </span><br>
                  <span :class="/[a-z]/.test(password1) ? 'text-success' : 'text-danger'">
                    <i class="bi" :class="/[a-z]/.test(password1) ? 'bi-check-circle-fill' : 'bi-x-circle'"></i> {{ $t('pwd_lowercase') }}
                  </span><br>
                  <span :class="/[0-9]/.test(password1) ? 'text-success' : 'text-danger'">
                    <i class="bi" :class="/[0-9]/.test(password1) ? 'bi-check-circle-fill' : 'bi-x-circle'"></i> {{ $t('pwd_number') }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-12 col-md-10 col-lg-8 mb-2">
              <label class="form-label mb-1">{{ $t('emailprefs.title') }}</label>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="pref-service" checked disabled />
                <label class="form-check-label small text-muted" for="pref-service">{{ $t('emailprefs.service') }}</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="pref-news" v-model="emailPrefs.news_and_announcements" />
                <label class="form-check-label small" for="pref-news">{{ $t('emailprefs.news_and_announcements') }}</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="pref-marketing" v-model="emailPrefs.marketing" />
                <label class="form-check-label small" for="pref-marketing">{{ $t('emailprefs.marketing') }}</label>
              </div>
            </div>
            <div class="col-12 col-md-10 col-lg-8 mb-3">
               <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="toc" v-model="hasAgreedToToS"  />
                    <label class="form-check-label" for="toc" v-html="$t('agree_terms', { link: '/weather-services-terms' })"></label>
                </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="button" class="btn btn-secondary" :disabled="isCreatingAccount" @click="reset">{{$t('reset')}}</button>
              <button type="button" class="btn btn-primary ms-2" :disabled="!hasCreateAccountConditions || isCreatingAccount || errorStatus" @click="createAccount">{{createAccountLabel}}</button>
              <a v-if="showCancel" :disabled="isCreatingAccount" class="alert-link px-2" href="#" @click="cancel()">{{ $t('cancel') }}</a>
            </div>
          </div>
        </template>
      </template>
    </div>
    </div>
   
 
</template>

<script>
//import CommonModals from '/src/components/CommonModals.vue'
import {VcValidation} from '/src/visualcrossing-core/vcvalidation';
import "/src/visualcrossing-core/vccommunicator_account";
import '/src/website-core/VcUtils';
import translations from '@/translations/signup.js';

// reCAPTCHA v3 site key - REQUIRED: Replace with your actual key from https://www.google.com/recaptcha/admin
// Also add the reCAPTCHA script tag to your HTML with your site key
const RECAPTCHA_SITE_KEY = '6LfK47orAAAAACnvv9p0jg3BxJMCHDzDDWtrdyLB';

export default {
  i18n: {
      messages: translations,
  },
  name: 'SignUp',
  emits: ['cancel','signupcomplete'],
  props: {
    showCancel:false,
    completionAction:null,
    completionPath:{
        type: String,
        default: "/weather-query-builder/"
      },
  },
  data() {
    return {
      email:null,
      password1:null,
      authCode:null,
      hasValidatedEmail:false,
      hasRequestedAuthCode:false,
      hasValidatedAuthCode:false,
      hasCreatedAccount:false,
      hasAgreedToToS:false,
      forwardLocation:null,
      requestedPlan:null,
      errorStatus:null,
      isCreatingAccount:false,
      isValidatingCaptcha:false,
      isValidatingAuthCode:false,
      showPassword:false,
      honeypot:null,
      autoLoginFailed:false,
      signupToken:null,
      signupSessionId:null,
      emailPrefs: { news_and_announcements: false, marketing: false },
    }
  },
  methods:{

    cancel() {
      this.$emit('cancel'); 
    },

    /**
     * Execute reCAPTCHA v3 and return the token.
     * In dev mode, returns null so the server skips verification
     * (requires server to have recaptchaSecretKey unconfigured).
     */
    async executeRecaptcha(action) {
      if (import.meta.env.DEV) {
        console.info('[DEV] Skipping reCAPTCHA verification');
        return null;
      }
      return new Promise((resolve, reject) => {
        if (typeof grecaptcha === 'undefined') {
          console.warn('reCAPTCHA not loaded');
          reject(new Error('reCAPTCHA not loaded. Please refresh the page and try again.'));
          return;
        }
        grecaptcha.ready(() => {
          grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
            .then(token => resolve(token))
            .catch(err => {
              console.error('reCAPTCHA error:', err);
              reject(err);
            });
        });
      });
    },

    reset() {
      var me=this;
      me.email=null;
      me.password1=null;
      me.authCode=null;
      me.hasValidatedEmail=false;
      me.hasRequestedAuthCode=false;
      me.hasValidatedAuthCode=false;
      me.hasCreatedAccount=false;
      me.hasAgreedToToS=false;
      me.errorStatus=null;
      me.showPassword=false;
      me.autoLoginFailed=false;
      me.emailPrefs = { news_and_announcements: false, marketing: false };
    },
    requestEmailValidation(retry) {
      var me=this;
      if (retry) {
        me.requestValidationCode();
        return;
      }
      me.errorStatus=null;
      me.hasValidatedAuthCode=false;
      me.isValidatingCaptcha=true;

      // Execute reCAPTCHA before making the request
      me.executeRecaptcha('signup_email_validation')
        .then(recaptchaToken => {
          me.isValidatingCaptcha=false;
          
          // Pass the reCAPTCHA token to the backend for verification
          vcCommunicator.validateuseremail(me.email, retry?true:false, recaptchaToken, null, me.signupToken, me.signupSessionId).then(function (result) {
            if (result && result.availableEmail) {
              me.hasValidatedEmail=true;
              if (result.sentValidationCode) {
                me.hasRequestedAuthCode=true;
              } else {
                me.requestValidationCode();
              }
            } else {
              me.hasValidatedEmail=false;
              me.errorStatus=result.info;
            }
          }).catch((error) => {
            me.hasValidatedEmail=false;
            me.errorStatus= me.$t("errors.validation");
            console.error("validateuseremail error:", error);
          });
        })
        .catch(error => {
          me.isValidatingCaptcha=false;
          me.errorStatus = me.$t("errors.captcha");
        });
    },

    requestValidationCode() {
      var me=this;
      
      let currentTime=new Date().getTime();
       
      if (me._validationRequests>10) {
        me.errorStatus="Email validation request limit exceeded.";
        return;
      }
      if ((currentTime-me._lastValidationRequest)<30000) {
        me.errorStatus=me.$t("validation_wait");
        return;
      }
      me.errorStatus=null;
      me._validationRequests++;

      me._lastValidationRequest=currentTime;

      me.hasRequestedAuthCode=true;
      me.errorStatus=null;
      vcCommunicator.generateAuthCode(me.email)
        .then(function (result) {

        }).catch((error) => {
          me.hasRequestedAuthCode=false;
          me.errorStatus=me.$t("errors.genauthcode") +error;
          //window.VcCommonModals.showError("Error requesting validation code", "There was a problem generating the authentication code. <br/>"+error);
          
        });
    },
   

    requestValidateValidationCode:function() {
      var me=this;
      me.hasValidatedAuthCode=false;
      if (!me.authCode || me.authCode.length!==5) {
         me.hasValidatedAuthCode=false;
        if (!me.authCode || me.authCode.length<5) {
          me.errorStatus=me.$t("validation_too_short");
        } else {
          me.errorStatus=me.$t("validation_too_long");
        }
       
        me.processingMsg=null;
        return;
      }
      me.isValidatingAuthCode=true;
      vcCommunicator.validateemail(me.email,me.authCode, null).then(function (result) {
        me.isValidatingAuthCode=false;
        if (result && result.validCode) {
          me.hasValidatedAuthCode=true;
          me.errorStatus=null;
        } else {
           me.errorStatus=me.$t("validation_incorrect");
          me.hasValidatedAuthCode=false;
        }
        
      }).catch((error) => {
        me.isValidatingAuthCode=false;
        me.hasValidatedAuthCode=false;
        me.errorStatus=me.$t("errors.validateemail") +error;
      });


    },

    createAccount:function() {
      var me=this;

      // Honeypot check - bots fill this hidden field
      if (me.honeypot) {
        // Silently pretend success to not tip off the bot
        me.hasCreatedAccount=true;
        return;
      }

      me.hasCreatedAccount=false;
      me.isCreatingAccount=true;
      me.errorStatus=null;

      // Execute reCAPTCHA before account creation
      me.executeRecaptcha('signup_create_account')
        .then(recaptchaToken => {
          return vcCommunicator.createnewclient(me.email, me.password1, me.authCode, null, recaptchaToken);
        })
        .then(function (result) {
        me.isCreatingAccount=false;
        try {
          if (window["gtag"]) {

            let domainCategory=(result && result.domainCategory) || "Unknown";

            gtag('event', 'sign_up', { method : 'Email', "domaincategory":domainCategory });

            gtag('set', 'user_data', {
              email: me.email  // plain text; gtag will hash it automatically
            });

            gtag('event', 'conversion', {
                'send_to': 'AW-1045373784/vk2eCMWmgboBENjGvPID',
                'event_callback': null
            });
            /*
            if (me.completionPath) {
              window.location=me.completionPath;
            } else if ("login"===me.completionAction) {
              const myModalEl = this.$el.querySelector("#signinModal");
              const modal = new Modal(myModalEl);
              modal.show();
            }
               me.$emit('signupcomplete'); 
            */
           
          
          }

        
        } catch (e) {
          console.error("createAccount error", e);
        }
        if (result && !result.errorCode) {
          me.hasCreatedAccount=true;

          vcCommunicator.createsession(me.email, me.password1)
            .then(() => {
              me.sessionId = vcCommunicator.sessionId;

              // Save email preferences after session is established
              if (me.emailPrefs.news_and_announcements || me.emailPrefs.marketing) {
                vcCommunicator.saveEmailPreferences(me.emailPrefs).catch((err) => {
                  console.warn('Could not save email preferences', err);
                });
              }

              var redirect = me.forwardLocation || me.completionPath;
              if (redirect) {
                window.location=redirect;
              }
            })
            .catch((error) => {
              me.autoLoginFailed=true;
            });


          /*
          if ("login"===me.completionAction) {
              vcCommunicator.createsession(me.email, me.password1)
                .then(() => {
                  console.log("Successful login");
                  me.sessionId = vcCommunicator.sessionId;

                  window.location=me.completionPath;
                })
                .catch((error) => {
                  console.log("Failed login");
                });
          } else if (me.completionPath) {
              window.location=me.completionPath;
          } else {

          }

          */
            
            me.$emit('signupcomplete'); 
        } else {
          me.errorStatus=result.message || me.$t("errors.createaccount") +" "+ result.errorCode;
        }

      
      }).catch((error) => {
        me.isCreatingAccount=false;
        me.errorStatus=me.$t("errors.createaccount")  +" "+ error;
      });
    }
  },
  computed:{
    createAccountLabel() {
      return this.$t(this.isCreatingAccount?'creatingaccount':'createaccount');
    },
    hasValidEmail() {
      return this.email && VcValidation.validateEmail(this.email);
    },
    hasPwdConditions() {
      var me=this;
      return me.hasValidEmail && VcValidation.validateAuthCode(me.authCode) && VcValidation.validatePassword(me.password1);
    },
    hasCreateAccountConditions() {
      var me=this;
      return me.hasPwdConditions && me.hasAgreedToToS;
    },
  },
  watch: {
    email:function() {
      this.hasValidatedEmail=false;
      this.hasRequestedAuthCode=false;
      this.hasValidatedAuthCode=false;
      this.authCode=null;
      this.password1=null;
      this.hasAgreedToToS=false;
      this.errorStatus=null;
    },
    authCode:function(val) {
      if (val && val.length === 5 && !this.isValidatingAuthCode) {
        this.requestValidateValidationCode();
      }
    },
    hasRequestedAuthCode:function(val) {
      if (val) this.$nextTick(() => this.$refs.authCodeInput?.focus());
    },
    hasValidatedAuthCode:function(val) {
      if (val) this.$nextTick(() => this.$refs.passwordInput?.focus());
    },
  },
  mounted() {
    var me=this;
      me._validationRequests=0;
      me._lastValidationRequest=0;
      me.forwardLocation=VcUtils.qs["from"] || null;
      me.requestedPlan=VcUtils.qs["pln"] || null;
      var vcCommunicator=window.vcCommunicator;
      if (!vcCommunicator) {
        console.error("No vcCommunicator");
        return;
      }
      me.sessionId=vcCommunicator.sessionId;
      vcCommunicator.addHandler('createsession', function (sessionId,sessionState) {
        me.sessionId=sessionId;
        me.sessionState=sessionState;
        /*
        var redirect="/weather-query-builder";
        if (me.requestedPlan) {
          redirect="/account"+"?pln="+me.requestedPlan;
        }
        window.location=redirect;
        */
      });
      vcCommunicator.addHandler('terminatesession', function () {
        me.sessionId=null;
        me.sessionState=null;
      });
      me.$nextTick(() => me.$refs.emailInput?.focus());

      // Fetch pre-flight signup token (for WAF + server validation)
      me._fetchSignupToken = function() {
        vcCommunicator.signupInit().then(function(result) {
          if (result && result.signupToken) {
            me.signupToken = result.signupToken;
            me.signupSessionId = result.signupSessionId;
          }
        }).catch(function(err) {
          console.warn('signup/init failed:', err);
        });
      };
      me._fetchSignupToken();

      // Auto-refresh token every 4 minutes (server TTL is 10 min)
      me._signupTokenRefreshTimer = setInterval(me._fetchSignupToken, 4 * 60 * 1000);
  },
  beforeUnmount() {
    if (this._signupTokenRefreshTimer) {
      clearInterval(this._signupTokenRefreshTimer);
    }
  },
  components: {
    //CommonModals
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .signup {
    min-height:400px;
    /*max-width:800px;*/
  }
</style>